import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import CaptionImage from "../components/CaptionImage";
import { Reveal, MaskedLines, Chapter } from "../components/Motion";
import { IMAGES } from "../site/content";
import { ArrowRight } from "lucide-react";

const TIMELINE = [
  {
    year: "2007",
    title: "A clinic in the desert",
    body: [
      "Daniel and Nora are in India on a US bank assignment. Nora, a neonatal nurse, begins volunteering at a desert-village clinic. Daniel joins her.",
      "There they meet Kabir — a local partner with an exact memory for every family on every track road. Kabir later marries Asha, and the two commit to serving remote communities full-time.",
    ],
  },
  {
    year: "2017",
    title: "The bond becomes family",
    body: [
      "Daniel returns and sees the work up close — the clinics, the school fees paid quietly, the rooftops rebuilt after floods. The partnership stops being a project and becomes family-like.",
      "Daniel and Nora keep coming back to support Kabir and Asha. Elena, Nora's sister, later joins the effort.",
    ],
  },
  {
    year: "Late 2025",
    title: "ProOne becomes a nonprofit",
    body: [
      "ProOne Solutions launches as a registered nonprofit — formalizing years of quiet field work into an organization that can receive support, report honestly, and grow carefully.",
      "The professional services arm grows alongside it, funding coordination and keeping the whole effort skill-rich: language, media, teaching, and care under one roof.",
    ],
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About — The People Behind ProOne"
        description="From a desert-village clinic in 2007 to a registered nonprofit in late 2025 — the story of the people behind ProOne Solutions, told with first names only."
        path="/about"
      />

      <section className="pt-28 sm:pt-36 pb-14 sm:pb-20" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-8 bg-teal" aria-hidden="true" />
            About ProOne
          </p>
          <MaskedLines
            lines={["Two arms,", <span key="t" className="italic text-teal">one promise.</span>]}
            className="mt-6 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-charcoal"
          />
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-charcoal-muted">
              A professional services and learning practice, and a registered nonprofit
              serving rural India — connected by the same people and the same standard of work.
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-charcoal-muted" data-testid="anonymity-note">
              A note on names: everyone here is introduced by first name only — Daniel and
              Nora, Kabir and Asha, Elena. We publish no surnames, no employers, and no
              identifiable village names. Dignity outranks storytelling.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16 sm:pb-24" data-testid="timeline-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Chapter number="01" eyebrow="The story" title="How a bank assignment became a life's work." />
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="space-y-12">
                {TIMELINE.map((t, i) => (
                  <Reveal key={t.year} delay={i * 0.05}>
                    <div className="relative border-l-2 border-teal/25 pl-7 sm:pl-10" data-testid={`timeline-${t.year.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span className="absolute -left-[7px] top-2 h-3 w-3 rotate-45 bg-teal" aria-hidden="true" />
                      <p className="font-mono text-sm tracking-[0.2em] uppercase text-teal">{t.year}</p>
                      <h3 className="mt-2 font-serif text-2xl sm:text-3xl font-semibold text-charcoal">{t.title}</h3>
                      {t.body.map((p, j) => (
                        <p key={j} className="mt-4 text-sm sm:text-base leading-relaxed text-charcoal/90">{p}</p>
                      ))}
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15} className="lg:sticky lg:top-28">
                <CaptionImage image={IMAGES.aboutSupplies} ratio="aspect-[4/5]" rounded="rounded-[2rem]" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-teal-dark" data-testid="about-closing">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="eyebrow !text-sand/80">Where that leaves us</p>
            <blockquote className="mx-auto mt-6 max-w-3xl font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold leading-snug text-cream" data-testid="about-closing-quote">
              "We come from medicine, education, business, and community service. We
              listen to local leaders and help deliver what works."
            </blockquote>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/our-work" className="btn-light" data-testid="about-see-work-btn">
                See the work <ArrowRight size={16} aria-hidden="true" />
              </Link>
              <Link to="/contact" className="btn-ghost-light" data-testid="about-contact-btn">
                Say hello
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
